import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Grid,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  ShoppingCart as ShoppingCartIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const UserServices = () => {
  const [services, setServices] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'services'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const servicesData = [];
      
      querySnapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        servicesData.push(data);
      });
      
      setServices(servicesData);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupServicesByCategory = () => {
    const grouped = {};
    services.forEach(service => {
      const category = service.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(service);
    });
    return grouped;
  };

  const getCategoryImage = (category) => {
    const images = {
      'Facial & Skin': 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400',
      'Salon & Beauty': 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=400',
      'Normal Haircut': 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=400',
      'Massage': 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400',
      'Beauty Therapy': 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=400'
    };
    return images[category] || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400';
  };

  const getServiceImage = (service) => {
    const defaultImages = {
      'Facial': 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400',
      'Skin Fade': 'https://images.unsplash.com/photo-1622286346003-c5fdaa4a1f0a?w=400',
      'Zero Fade + Beard Trim': 'https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=400',
      'Head Shave': 'https://images.unsplash.com/photo-1621605815971-fbc98d665033?w=400',
      'Normal Haircut': 'https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=400'
    };
    
    return service.imageUrl || defaultImages[service.title] || defaultImages[service.name] || getCategoryImage(service.category);
  };

  const getServiceImageSx = (service) => ({
    objectFit: service?.imageFit || 'cover',
    objectPosition: `${service?.imagePositionX ?? 50}% ${service?.imagePositionY ?? 50}%`,
  });

  const getServiceDuration = (service) => {
    if (service.duration) return service.duration;
    const durations = {
      'Facial': '45m',
      'Skin Fade': '50m',
      'Zero Fade + Beard Trim': '75m',
      'Head Shave': '30m',
      'Normal Haircut': '30m',
      'Massage': '60m',
      'Beauty Therapy': '45m'
    };
    return durations[service.title] || durations[service.name] || '45m';
  };

  const handleAddToCart = (service) => {
    setCart(prev => [...prev, service]);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (selectedCategory) {
    const categoryServices = services.filter(s => s.category === selectedCategory);
    
  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header with Back Button */}
      <Box sx={{ 
        backgroundColor: '#E3F2FD', 
        p: 3, 
        mx: 4,
        mt: 3,
        borderRadius: 3, 
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => setSelectedCategory(null)}
            sx={{ 
              backgroundColor: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.75rem' }}>
              {selectedCategory}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Select a service and provider to get started
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 18, color: '#666' }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#666' }}>
              {categoryServices.length} services available
            </Typography>
          </Box>
          <IconButton sx={{ backgroundColor: 'white', '&:hover': { backgroundColor: '#f5f5f5' } }}>
            <Badge badgeContent={cart.length} color="error">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>
        </Box>
      </Box>

      {/* Services Grid */}
      <Box sx={{ px: 4 }}>
        <Grid container spacing={3}>
          {categoryServices.map((service) => {
            const price = parseFloat(service.price || 0);
            const discount = parseFloat(service.discount || 0);
            const finalPrice = discount > 0 ? price - (price * discount / 100) : price;
            const duration = getServiceDuration(service);
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={service.id}>
                <Card sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  overflow: 'hidden',
                  boxShadow: 'none',
                  border: '1px solid #e8e8e8',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                  }
                }}>
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="180"
                      image={getServiceImage(service)}
                      alt={service.title || service.name}
                      sx={getServiceImageSx(service)}
                    />
                    <Box sx={{ 
                      position: 'absolute', 
                      top: 12, 
                      left: 12,
                      display: 'flex',
                      gap: 1
                    }}>
                      <Chip
                        label={`$${finalPrice.toFixed(0)}`}
                        sx={{ 
                          backgroundColor: 'white',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          height: 28,
                          '& .MuiChip-label': { px: 1.5 }
                        }}
                      />
                      <Chip
                        icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                        label={duration}
                        sx={{ 
                          backgroundColor: 'white',
                          fontSize: '0.75rem',
                          height: 28,
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    </Box>
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 2.5, pb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.1rem' }}>
                      {service.title || service.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.8rem' }}>
                      {service.category} • {selectedCategory}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.85rem', lineHeight: 1.5 }}>
                      {service.description || 'Professional service'}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2.5, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<ShoppingCartIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handleAddToCart(service)}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1.2,
                        fontSize: '0.9rem',
                        backgroundColor: '#5B67F5',
                        '&:hover': {
                          backgroundColor: '#4A5BF4'
                        }
                      }}
                    >
                      Book Service
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
    );
  }

  const groupedServices = groupServicesByCategory();

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: '1.75rem' }}>
          Book Services
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          Select a service to book
        </Typography>
      </Box>

      {/* Categories Grid */}
      <Box sx={{ px: 4 }}>
        <Grid container spacing={3}>
          {Object.keys(groupedServices).map((category) => {
            const categoryServices = groupedServices[category];
            const serviceCount = categoryServices.length;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={category}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 'none',
                    border: '1px solid #e8e8e8',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.1)'
                    }
                  }}
                  onClick={() => setSelectedCategory(category)}
                >
                <CardMedia
                  component="img"
                  height="220"
                  image={getCategoryImage(category)}
                  alt={category}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5, fontSize: '1.3rem' }}>
                    {category}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.9rem', lineHeight: 1.6 }}>
                    {categoryServices[0]?.description || `Choose from ${serviceCount} available service types`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StarIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32', fontSize: '0.85rem' }}>
                        Available
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Cart Button */}
      {cart.length > 0 && (
        <Box sx={{ 
          position: 'fixed', 
          bottom: 24, 
          right: 24,
          zIndex: 1000
        }}>
          <Button
            variant="contained"
            size="large"
            startIcon={
              <Badge badgeContent={cart.length} color="error">
                <ShoppingCartIcon />
              </Badge>
            }
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 3,
              boxShadow: 3,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            View Cart
          </Button>
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default UserServices;
